import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, UserPlus, Filter } from "lucide-react";
import { API } from "@/contexts/AuthContext";
import { StatusBadge } from "./StaffDashboard";
import ClientFormModal from "./ClientFormModal";

const TEAM_LABELS = {
  ndis: { label: "NDIS", bg: "#DDE7E9", text: "#284952" },
  dva: { label: "DVA", bg: "#FCEADB", text: "#A0521A" },
  dfv: { label: "D&FV", bg: "#FCE7EB", text: "#A12545" },
  community: { label: "Community", bg: "#E8F1E5", text: "#1F6B3A" },
  other: { label: "Other", bg: "#F0E5DE", text: "#555" },
};

export function TeamBadge({ team }) {
  const t = TEAM_LABELS[team] || TEAM_LABELS.other;
  return (
    <span
      className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full"
      style={{ background: t.bg, color: t.text }}
      data-testid={`team-badge-${team || "other"}`}
    >
      {t.label}
    </span>
  );
}

export default function ClientsList() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [teamFilter, setTeamFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/clients`);
      setClients(data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const filtered = clients.filter((c) => {
    const matchSearch = !search || `${c.first_name} ${c.last_name} ${c.ndis_number || ""} ${c.email || ""}`.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || c.status === statusFilter;
    const matchTeam = teamFilter === "all" || c.team === teamFilter;
    return matchSearch && matchStatus && matchTeam;
  });

  return (
    <div data-testid="clients-list-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-sm uppercase tracking-wider text-[#EA823A] font-semibold mb-2">Caseload</div>
          <h1 className="font-display text-4xl text-[#1F2A2E]">Clients</h1>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary" data-testid="add-client-btn">
          <UserPlus className="w-4 h-4" /> Add client
        </button>
      </div>

      <div className="bg-white rounded-3xl border border-[#F0E5DE] mb-6 p-5 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#6B7A80]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, NDIS # or email..."
            className="input-navica pl-10"
            data-testid="client-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-[#6B7A80]" />
          <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} className="input-navica !py-2.5" data-testid="team-filter">
            <option value="all">All teams</option>
            <option value="ndis">NDIS</option>
            <option value="dva">DVA</option>
            <option value="dfv">D&FV</option>
            <option value="community">Community Events</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-[#6B7A80]" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-navica !py-2.5" data-testid="status-filter">
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="on_hold">On hold</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20"><div className="w-8 h-8 border-3 border-[#E2718A] border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-[#F0E5DE]" data-testid="clients-empty">
          <UserPlus className="w-12 h-12 mx-auto text-[#F4C3CC] mb-4" />
          <h3 className="font-display text-xl text-[#1F2A2E] mb-2">No clients yet</h3>
          <p className="text-[#6B7A80] mb-6 text-sm">Add your first client to start tracking their journey.</p>
          <button onClick={() => setShowForm(true)} className="btn-primary">Add a client</button>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-[#F0E5DE] overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#F1E4D5] text-left">
              <tr>
                <Th>Name</Th>
                <Th>Team</Th>
                <Th>NDIS # / Date</Th>
                <Th>Assigned</Th>
                <Th>Status</Th>
                <Th>Updated</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0E5DE]">
              {filtered.map((c) => {
                const isEvent = c.team === "community";
                return (
                  <tr key={c.id} className="hover:bg-[#F1E4D5]/50 transition">
                    <td className="px-5 py-4">
                      <Link to={`/staff/clients/${c.id}`} className="font-medium text-[#1F2A2E] hover:text-[#E2718A] transition" data-testid={`client-row-${c.id}`}>
                        {isEvent ? c.first_name : `${c.first_name} ${c.last_name}`}
                      </Link>
                      {isEvent
                        ? (c.event_location && <div className="text-xs text-[#6B7A80] mt-0.5">{c.event_location}</div>)
                        : (c.email && <div className="text-xs text-[#6B7A80] mt-0.5">{c.email}</div>)}
                    </td>
                    <td className="px-5 py-4"><TeamBadge team={c.team || "ndis"} /></td>
                    <td className="px-5 py-4 text-sm text-[#284952]">
                      {isEvent
                        ? (c.event_date ? <span className="font-mono">{c.event_date}</span> : <span className="text-[#6B7A80]">-</span>)
                        : <span className="font-mono">{c.ndis_number || "-"}</span>}
                    </td>
                    <td className="px-5 py-4 text-sm text-[#284952]">{c.assigned_to_name || <span className="text-[#6B7A80]">Unassigned</span>}</td>
                    <td className="px-5 py-4"><StatusBadge status={c.status} /></td>
                    <td className="px-5 py-4 text-xs text-[#6B7A80]">{new Date(c.updated_at).toLocaleDateString("en-AU")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <ClientFormModal
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); fetchClients(); }}
        />
      )}
    </div>
  );
}

function Th({ children }) {
  return <th className="px-5 py-3 text-xs uppercase tracking-wider font-semibold text-[#6B7A80]">{children}</th>;
}
